import type { AttachmentKind, AttachmentPurpose, GenerateAttachment } from '@/lib/generate';

const MAX_TEXT_EXTRACT = 20_000;
const PDF_MAX_PAGES = 20;

export interface PendingAttachmentFile {
  file: File;
  purpose: AttachmentPurpose;
}

export async function extractTextFromPdf(file: File): Promise<string> {
  // @ts-ignore
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).toString();

  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  const pageCount = Math.min(doc.numPages, PDF_MAX_PAGES);
  const parts: string[] = [];

  for (let i = 1; i <= pageCount; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: any) => ('str' in item ? item.str : ''))
      .join(' ')
      .trim();
    if (pageText) parts.push(pageText);
  }

  const text = parts.join('\n\n').trim();
  if (!text) {
    throw new Error('No readable text in PDF. Try an image or text file.');
  }
  if (text.length > MAX_TEXT_EXTRACT) {
    return `${text.slice(0, MAX_TEXT_EXTRACT)}\n\n[...truncated...]`;
  }
  return text;
}

export async function readFileAsText(file: File): Promise<string> {
  const text = await file.text();
  if (text.length > MAX_TEXT_EXTRACT) {
    return `${text.slice(0, MAX_TEXT_EXTRACT)}\n\n[...truncated...]`;
  }
  return text;
}

function inferKind(file: File): AttachmentKind | null {
  const mime = file.type.toLowerCase();
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime === 'text/csv' || ext === 'csv') return 'csv';
  if (mime === 'text/markdown' || ext === 'md' || ext === 'markdown') return 'markdown';
  if (mime.startsWith('text/') || ext === 'txt') return 'text';
  return null;
}

export function isSupportedReferenceFile(file: File): boolean {
  return inferKind(file) !== null;
}

export async function analyzeReferenceFile(
  file: File,
  purpose: AttachmentPurpose,
): Promise<GenerateAttachment> {
  const kind = inferKind(file);
  if (!kind) {
    throw new Error('Unsupported file type');
  }

  if (kind === 'image') {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purpose', purpose);
    const res = await fetch('/api/attachments/analyze', {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error ?? `Upload failed (${res.status})`);
    }
    return (await res.json()) as GenerateAttachment;
  }

  let text: string;
  if (kind === 'pdf') {
    text = await extractTextFromPdf(file);
  } else {
    text = await readFileAsText(file);
  }

  const res = await fetch('/api/attachments/analyze', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: file.name,
      mime: file.type || 'text/plain',
      text,
      purpose,
    }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error ?? `Analysis failed (${res.status})`);
  }
  return (await res.json()) as GenerateAttachment;
}

export const SOURCE_MAX_CHARS = 20_000;
export const SOURCE_STRUCTURE_THRESHOLD = 8_000;

export function sourceContentStatus(length: number): {
  label: string;
  tone: 'ok' | 'warn' | 'error';
  buttonLabel: string;
} {
  if (length > SOURCE_MAX_CHARS) {
    return {
      label: 'Too long: upload as a file or shorten',
      tone: 'error',
      buttonLabel: 'Format into email',
    };
  }
  if (length > SOURCE_STRUCTURE_THRESHOLD) {
    return {
      label: 'Long copy: will be structured first',
      tone: 'warn',
      buttonLabel: 'Structure and format',
    };
  }
  if (length >= 8) {
    return {
      label: 'Ready to format',
      tone: 'ok',
      buttonLabel: 'Format into email',
    };
  }
  return {
    label: 'Paste at least 8 characters',
    tone: 'ok',
    buttonLabel: 'Format into email',
  };
}
